import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sparkles, AlertCircle, Globe } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { getCreatorPublicProfile, logCreatorLandingVisit } from '../../utils/creatorApi';
import type { CreatorPublicProfile } from '../../types/creator';

const SOCIAL_LABELS: Record<string, string> = {
  twitter: 'X',
  github: 'GitHub',
  website: 'Website',
  linkedin: 'LinkedIn',
};

function normalizeSocialUrl(key: string, raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (key === 'twitter') {
    const handle = v.replace(/^@/, '');
    return `https://x.com/${handle}`;
  }
  if (key === 'github') return `https://github.com/${v.replace(/^@/, '')}`;
  if (key === 'linkedin') return v.startsWith('linkedin.com') ? `https://${v}` : `https://linkedin.com/in/${v}`;
  return `https://${v}`;
}

export function CreatorPublicProfilePage() {
  const { handle } = useParams<{ handle: string }>();
  const [profile, setProfile] = useState<CreatorPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const data = await getCreatorPublicProfile(handle);
      if (cancelled) return;
      if (!data) {
        setError('Creator not found');
      } else {
        setProfile(data);
        void logCreatorLandingVisit({
          handle,
          referrer: typeof document !== 'undefined' ? document.referrer || null : null,
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const socials = useMemo(() => {
    const raw = profile?.creator_socials || {};
    return Object.entries(raw)
      .map(([k, v]) => ({ key: k, label: SOCIAL_LABELS[k] || k, url: normalizeSocialUrl(k, String(v)) }))
      .filter((s) => Boolean(s.url));
  }, [profile?.creator_socials]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6 text-center gap-3">
        <AlertCircle className="w-8 h-8 text-status-error" />
        <p className="text-th-secondary">{error || 'Creator not available'}</p>
        <Link to="/" className="text-brand hover:underline">
          Back to store
        </Link>
      </div>
    );
  }

  const templates = profile.templates || [];

  return (
    <div className="min-h-dvh max-w-5xl mx-auto px-4 pt-8 pb-20">
      <header className="card p-6 flex flex-wrap items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-brand/15 flex items-center justify-center shrink-0 text-2xl font-bold text-brand">
          {profile.hy_name.slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-th-primary tracking-tight truncate">
            {profile.hy_name}
          </h1>
          <p className="text-sm text-th-secondary font-mono mt-1">@{profile.creator_handle}</p>
          {profile.creator_bio && (
            <p className="text-sm text-th-secondary whitespace-pre-wrap mt-3">{profile.creator_bio}</p>
          )}
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={s.url || undefined}
                  target="_blank"
                  rel="noreferrer noopener nofollow"
                  className="text-xs px-2.5 py-1 rounded-full border border-subtle bg-elevated text-th-secondary hover:border-brand/50 hover:text-th-primary inline-flex items-center gap-1.5"
                >
                  <Globe className="w-3 h-3" />
                  {s.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </header>

      <section className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-th-secondary mb-3">
          Live agents
        </h2>
        {templates.length === 0 ? (
          <p className="text-sm text-th-secondary">No live agents yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((t) => {
              const url = t.slug ? `/c/${profile.creator_handle}/${t.slug}` : `/apps/agent/${t.id}`;
              return (
                <Link
                  key={t.id}
                  to={url}
                  className="card p-4 flex flex-col gap-2 hover:border-brand/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-elevated overflow-hidden flex items-center justify-center shrink-0">
                      {t.cover_media_url && t.cover_media_type === 'image' ? (
                        <img src={t.cover_media_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Sparkles className="w-5 h-5 text-brand" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-th-primary truncate">
                        {t.public_title || t.name}
                      </h3>
                      {t.public_description && (
                        <p className="text-xs text-th-secondary line-clamp-2 mt-0.5">
                          {t.public_description}
                        </p>
                      )}
                    </div>
                  </div>
                  {(t.tags?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(t.tags || []).slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-overlay text-th-secondary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
